import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FileText, Download, CheckCircle2, Loader2, ShieldCheck, Clock, Receipt, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api, API, formatApiError } from "@/lib/api";
import { PQR_TYPES, RECURSO_TYPES, ID_TYPES } from "@/data/content";

const empty = {
  nombre: "", tipo_identificacion: "Cédula de ciudadanía", identificacion: "", email: "",
  telefono: "", direccion: "", tipo_pqr: "Petición", recurso: "", relacionada_facturacion: false,
  motivo: "", descripcion: "",
};

export default function PQR() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.identificacion || !form.email || !form.telefono || !form.motivo || !form.descripcion) {
      toast.error("Completa todos los campos obligatorios.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (payload.tipo_pqr !== "Recurso") payload.recurso = "";
      const { data } = await api.post("/pqr", payload);
      setResult(data);
      toast.success(`PQR radicada. Radicado ${data.radicado}`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo radicar la PQR.");
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16" data-testid="pqr-success">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="rounded-3xl border border-border bg-card p-8 sm:p-12 text-center">
          <div className="h-16 w-16 rounded-full bg-emerald-500/10 grid place-items-center mx-auto mb-6"><CheckCircle2 className="h-9 w-9 text-emerald-500" /></div>
          <h1 className="font-display text-3xl font-black tracking-tight">¡PQR radicada con éxito!</h1>
          <p className="text-muted-foreground mt-2">Guarda tu número de radicado para hacer seguimiento.</p>
          <div className="my-8 rounded-2xl bg-accent/60 border border-primary/20 p-6">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Número de radicado</div>
            <div className="font-mono text-3xl font-bold text-primary" data-testid="pqr-radicado">{result.radicado}</div>
            <div className="grid sm:grid-cols-2 gap-4 mt-6 text-left">
              <div className="flex items-start gap-2"><Clock className="h-4 w-4 text-primary mt-0.5" /><div><div className="text-xs text-muted-foreground">Plazo de respuesta</div><div className="font-semibold text-sm">{result.plazo_respuesta}</div></div></div>
              <div className="flex items-start gap-2"><FileText className="h-4 w-4 text-primary mt-0.5" /><div><div className="text-xs text-muted-foreground">Fecha de radicación</div><div className="font-semibold text-sm">{new Date(result.fecha_radicacion).toLocaleString("es-CO")}</div></div></div>
            </div>
          </div>
          <div className="rounded-2xl bg-muted/50 p-5 text-left text-sm text-muted-foreground mb-8">
            <p className="font-semibold text-foreground mb-2 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Silencio Administrativo Positivo</p>
            <p className="leading-relaxed">{result.silencio_administrativo}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href={`${API}/pqr/${result.radicado}/constancia`} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="rounded-full font-semibold gap-2 w-full" data-testid="pqr-download-btn"><Download className="h-4 w-4" /> Descargar constancia (PDF)</Button>
            </a>
            <Button size="lg" variant="outline" className="rounded-full font-semibold" onClick={() => { setResult(null); setForm(empty); }} data-testid="pqr-new-btn">Radicar otra PQR</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12" data-testid="pqr-page">
      <div className="mb-10">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Radicación en línea</span>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Peticiones, Quejas, Reclamos y Recursos</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">Diligencia el formulario y recibe tu constancia de radicación con número de radicado y plazo de respuesta.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 items-start">
        <form onSubmit={submit} className="lg:col-span-2 rounded-3xl border border-border bg-card p-6 sm:p-8 space-y-5" data-testid="pqr-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Nombre completo *"><Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="pqr-nombre" placeholder="Ej: María Gómez" /></Field>
            <Field label="Tipo de identificación *">
              <Select value={form.tipo_identificacion} onValueChange={(v) => set("tipo_identificacion", v)}>
                <SelectTrigger data-testid="pqr-tipo-id"><SelectValue /></SelectTrigger>
                <SelectContent>{ID_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Número de identificación *"><Input value={form.identificacion} onChange={(e) => set("identificacion", e.target.value)} data-testid="pqr-identificacion" placeholder="Ej: 1020304050" /></Field>
            <Field label="Correo electrónico *"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="pqr-email" placeholder="tucorreo@ejemplo.com" /></Field>
            <Field label="Teléfono de contacto *"><Input value={form.telefono} onChange={(e) => set("telefono", e.target.value)} data-testid="pqr-telefono" placeholder="Ej: 300 000 0000" /></Field>
            <Field label="Dirección"><Input value={form.direccion} onChange={(e) => set("direccion", e.target.value)} data-testid="pqr-direccion" placeholder="Opcional" /></Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Tipo de PQR *">
              <Select value={form.tipo_pqr} onValueChange={(v) => set("tipo_pqr", v)}>
                <SelectTrigger data-testid="pqr-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{PQR_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {form.tipo_pqr === "Recurso" && (
              <Field label="Recurso *">
                <Select value={form.recurso} onValueChange={(v) => set("recurso", v)}>
                  <SelectTrigger data-testid="pqr-recurso"><SelectValue placeholder="Selecciona el recurso" /></SelectTrigger>
                  <SelectContent>{RECURSO_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
          </div>

          <Field label="Motivo de la solicitud *"><Input value={form.motivo} onChange={(e) => set("motivo", e.target.value)} data-testid="pqr-motivo" placeholder="Ej: Cobro no reconocido en la factura" /></Field>
          <Field label="Descripción detallada *"><Textarea rows={5} value={form.descripcion} onChange={(e) => set("descripcion", e.target.value)} data-testid="pqr-descripcion" placeholder="Describe con detalle tu solicitud..." /></Field>

          <label className="flex items-center gap-3 text-sm cursor-pointer">
            <Checkbox checked={form.relacionada_facturacion} onCheckedChange={(v) => set("relacionada_facturacion", !!v)} data-testid="pqr-facturacion" />
            Esta PQR está relacionada con facturación
          </label>

          <Button type="submit" size="lg" disabled={loading} className="w-full rounded-full font-semibold gap-2 h-13 text-base" data-testid="pqr-submit">
            {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Radicando...</> : <><FileText className="h-5 w-5" /> Radicar PQR</>}
          </Button>
          <p className="text-xs text-muted-foreground text-center">Al radicar aceptas el tratamiento de tus datos personales conforme a la política de privacidad de Colnexx.</p>
        </form>

        <aside className="space-y-4">
          <InfoCard icon={Clock} title="Plazo de respuesta" text="Toda PQR se responde dentro de los 15 días hábiles siguientes a su radicación." />
          <InfoCard icon={Receipt} title="PQR de facturación" text="Mientras se resuelve tu reclamo no debes pagar las sumas objeto de reclamación; solo los valores no reclamados." />
          <InfoCard icon={Scale} title="Recursos" text="Frente a la respuesta puedes interponer recurso de reposición y, en subsidio, apelación." />
          <InfoCard icon={ShieldCheck} title="Silencio Administrativo Positivo" text="Si no recibes respuesta oportuna, la PQR se entiende resuelta a tu favor." />
        </aside>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-2"><Label className="text-sm">{label}</Label>{children}</div>
);

const InfoCard = ({ icon: Icon, title, text }) => (
  <div className="rounded-2xl border border-border bg-card p-5">
    <div className="flex items-center gap-2 mb-2"><Icon className="h-4 w-4 text-primary" /><span className="font-display font-bold text-sm">{title}</span></div>
    <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
  </div>
);
